import React from "react";
import { updateLoan, resetLoan } from "../../services/loanApi";
import { formatKstShort } from "../../utils/time";

// DAS-182: "정말 초기화" 재클릭 유예 시간 (브라우저 confirm/alert 대신 2단계 클릭으로 대체)
const RESET_CONFIRM_WINDOW_MS = 5000;

const formatKrw0 = (n) => {
    if (!Number.isFinite(n)) return "—";
    return "₩" + Math.round(n).toLocaleString();
};

const formatSignedKrw = (n) => {
    if (!Number.isFinite(n)) return "—";
    const rounded = Math.round(n);
    if (rounded === 0) return "₩0";
    const sign = rounded > 0 ? "+" : "-";
    return `${sign}₩${Math.abs(rounded).toLocaleString()}`;
};

const netToneClass = (n) => {
    if (!Number.isFinite(n)) return "text-content-muted";
    const rounded = Math.round(n);
    if (rounded === 0) return "text-content-muted";
    return rounded > 0 ? "text-gain" : "text-loss";
};

const formatQty = (n, maxDecimals = 4) => {
    if (!Number.isFinite(n)) return "—";
    return n.toLocaleString(undefined, { maximumFractionDigits: maxDecimals });
};

// 담보 코인 select 고정 옵션 — 그 외 통화는 "기타" 선택 후 자유 텍스트로 입력한다.
const KNOWN_COLLATERAL_CURRENCIES = ["BTC", "ETH", "USDT"];

/**
 * DAS-182: 코인빌리기(담보·대여) 잔차 추정 블록.
 * props.loan 이 없으면(추정 시작 전) 아무것도 렌더하지 않는다 — 게이팅은 부모(position_table)에서도 하지만
 * 방어적으로 한 번 더 체크한다.
 */
class LoanPanel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            editing: false,
            submitting: false,
            error: null,
            statusMessage: null,
            resetArmed: false,
            form: this.buildForm(props.loan),
        };
        this.resetTimer = null;
    }

    componentWillUnmount() {
        if (this.resetTimer) {
            clearTimeout(this.resetTimer);
            this.resetTimer = null;
        }
    }

    buildForm = (loan) => {
        const firstBorrowed = Array.isArray(loan?.borrowed) ? loan.borrowed[0] : null;
        const firstCollateralCoin = Array.isArray(loan?.collateral_coins) ? loan.collateral_coins[0] : null;
        const collateralCoinCurrency = firstCollateralCoin?.currency
            ? (KNOWN_COLLATERAL_CURRENCIES.includes(firstCollateralCoin.currency) ? firstCollateralCoin.currency : "OTHER")
            : "";
        const overrideEntries = loan?.avg_price_override && typeof loan.avg_price_override === "object"
            ? Object.entries(loan.avg_price_override)
            : [];
        const firstOverride = overrideEntries[0] || null;
        const avgPriceCurrency = firstOverride
            ? (KNOWN_COLLATERAL_CURRENCIES.includes(firstOverride[0]) ? firstOverride[0] : "OTHER")
            : "";
        return {
            collateralKrw: loan?.collateral_krw != null ? String(Math.round(loan.collateral_krw)) : "0",
            currency: firstBorrowed?.currency || "USDT",
            qty: firstBorrowed?.qty != null ? String(firstBorrowed.qty) : "0",
            // DAS-182 계약 보완: 담보 코인(collateral_coins) 1행 — 수량을 비우면 저장 시 {}로 전송된다.
            collateralCoinCurrency,
            collateralCoinCustom: collateralCoinCurrency === "OTHER" ? firstCollateralCoin.currency : "",
            collateralCoinQty: firstCollateralCoin?.qty != null ? String(firstCollateralCoin.qty) : "",
            // DAS-182 계약 보완: 평단 보정(avg_price_override) 1행 — 값을 비우거나 0이면 해당 통화 제거한다.
            avgPriceCurrency,
            avgPriceCustom: avgPriceCurrency === "OTHER" ? firstOverride[0] : "",
            avgPriceValue: firstOverride ? String(firstOverride[1]) : "",
        };
    };

    startEdit = () => {
        this.setState({
            editing: true,
            error: null,
            statusMessage: null,
            form: this.buildForm(this.props.loan),
        });
    };

    cancelEdit = () => {
        this.setState({ editing: false, error: null });
    };

    handleFieldChange = (field) => (event) => {
        const { value } = event.target;
        this.setState((prev) => ({ form: { ...prev.form, [field]: value } }));
    };

    submitEdit = async () => {
        const { username, onChanged, loan } = this.props;
        const { form } = this.state;
        const collateralKrw = Number(form.collateralKrw);
        const qty = Number(form.qty);

        if (!Number.isFinite(collateralKrw) || collateralKrw < 0) {
            this.setState({ error: "담보 원화는 0 이상 숫자로 입력하세요." });
            return;
        }
        if (!form.currency) {
            this.setState({ error: "대여 통화를 선택하세요." });
            return;
        }
        if (!Number.isFinite(qty) || qty < 0) {
            this.setState({ error: "대여 수량은 0 이상 숫자로 입력하세요." });
            return;
        }

        // 담보 코인 — 수량을 비우면 담보 코인 없음({})으로 저장한다.
        let collateralCoins = {};
        const collateralQtyRaw = form.collateralCoinQty.trim();
        if (collateralQtyRaw !== "") {
            const collateralCoinCurrency =
                form.collateralCoinCurrency === "OTHER"
                    ? form.collateralCoinCustom.trim().toUpperCase()
                    : form.collateralCoinCurrency;
            const collateralQty = Number(collateralQtyRaw);
            if (!collateralCoinCurrency) {
                this.setState({ error: "담보 코인 통화를 선택하거나 입력하세요." });
                return;
            }
            if (!Number.isFinite(collateralQty) || collateralQty < 0) {
                this.setState({ error: "담보 코인 수량은 0 이상 숫자로 입력하세요." });
                return;
            }
            collateralCoins = { [collateralCoinCurrency]: collateralQty };
        }

        const payload = {
            collateral_krw: collateralKrw,
            collateral_coins: collateralCoins,
            borrowed: { [form.currency]: qty },
        };

        // CRITICAL-2 리뷰 반영: 기존 담보 코인의 평단(avg_buy_price)이 있으면 함께 보내 유지시킨다.
        // (편집 중인 통화와 같은 기존 항목만 대상 — 값이 없으면 필드 자체를 생략한다.)
        const editedCollateralCurrency = Object.keys(collateralCoins)[0];
        if (editedCollateralCurrency) {
            const existingCoin = (Array.isArray(loan?.collateral_coins) ? loan.collateral_coins : []).find(
                (c) => c.currency === editedCollateralCurrency
            );
            if (existingCoin && Number.isFinite(existingCoin.avg_buy_price)) {
                payload.collateral_avg_price = { [editedCollateralCurrency]: existingCoin.avg_buy_price };
            }
        }

        // 평단 보정(avg_price_override) — 기존 맵을 복사해 편집한 통화만 갱신/제거한다(PUT은 전체 교체이므로
        // 건드리지 않은 다른 통화의 override는 그대로 유지해서 보낸다).
        const avgPriceOverride = {
            ...(loan?.avg_price_override && typeof loan.avg_price_override === "object" ? loan.avg_price_override : {}),
        };
        const avgPriceCurrency =
            form.avgPriceCurrency === "OTHER" ? form.avgPriceCustom.trim().toUpperCase() : form.avgPriceCurrency;
        const avgPriceValueRaw = form.avgPriceValue.trim();
        if (avgPriceCurrency) {
            if (avgPriceValueRaw === "") {
                delete avgPriceOverride[avgPriceCurrency];
            } else {
                const avgPriceValue = Number(avgPriceValueRaw);
                if (!Number.isFinite(avgPriceValue) || avgPriceValue < 0) {
                    this.setState({ error: "평단 보정 값은 0 이상 숫자로 입력하세요." });
                    return;
                }
                if (avgPriceValue === 0) {
                    delete avgPriceOverride[avgPriceCurrency];
                } else {
                    avgPriceOverride[avgPriceCurrency] = avgPriceValue;
                }
            }
        } else if (avgPriceValueRaw !== "") {
            this.setState({ error: "평단 보정 통화를 선택하거나 입력하세요." });
            return;
        }
        payload.avg_price_override = avgPriceOverride;

        this.setState({ submitting: true, error: null, statusMessage: "저장 중..." });
        try {
            await updateLoan(username, payload);
            this.setState({ editing: false, submitting: false, statusMessage: "보정이 저장되었습니다." });
            if (onChanged) onChanged();
        } catch (err) {
            this.setState({
                submitting: false,
                statusMessage: null,
                error: err?.response?.data?.message || "보정 저장에 실패했습니다.",
            });
        }
    };

    // 브라우저 confirm/alert 대신 2단계 클릭: 첫 클릭은 "정말 초기화"로 라벨만 바뀌고,
    // RESET_CONFIRM_WINDOW_MS 내에 재클릭해야 실제 DELETE가 실행된다.
    armReset = () => {
        if (this.state.resetArmed) {
            this.executeReset();
            return;
        }
        this.setState({ resetArmed: true, error: null });
        this.resetTimer = setTimeout(() => {
            this.setState({ resetArmed: false });
            this.resetTimer = null;
        }, RESET_CONFIRM_WINDOW_MS);
    };

    executeReset = async () => {
        if (this.resetTimer) {
            clearTimeout(this.resetTimer);
            this.resetTimer = null;
        }
        const { username, onChanged } = this.props;
        this.setState({ submitting: true, error: null, resetArmed: false, statusMessage: "초기화 중..." });
        try {
            await resetLoan(username);
            this.setState({ submitting: false, statusMessage: "코인빌리기 추정이 초기화되었습니다." });
            if (onChanged) onChanged();
        } catch (err) {
            this.setState({
                submitting: false,
                statusMessage: null,
                error: err?.response?.data?.message || "초기화에 실패했습니다.",
            });
        }
    };

    render() {
        const { loan } = this.props;
        if (!loan) return null;

        const { editing, submitting, error, statusMessage, resetArmed, form } = this.state;
        const borrowedList = Array.isArray(loan.borrowed) ? loan.borrowed : [];
        const collateralCoinList = Array.isArray(loan.collateral_coins) ? loan.collateral_coins : [];
        const lastEventAt = loan.last_event_at ? formatKstShort(loan.last_event_at) : null;
        // DAS-182 계약 보완: 평단 보정(avg_price_override) — 담보·대여가 모두 0이고 override만 있으면
        // "업비트 보정" 블록으로 표시하고 담보/대여/순효과 행은 생략한다.
        const avgPriceOverrideEntries =
            loan.avg_price_override && typeof loan.avg_price_override === "object"
                ? Object.entries(loan.avg_price_override).filter(([, v]) => Number(v) > 0)
                : [];
        const hasCollateralOrBorrowed =
            Number(loan.collateral_krw) > 0 || collateralCoinList.length > 0 || borrowedList.length > 0;
        const isOverrideOnly = !hasCollateralOrBorrowed && avgPriceOverrideEntries.length > 0;

        return (
            <div className="mt-3 rounded-xl border border-surface-border bg-surface-card p-4 sm:p-5 animate-fade-in">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-content">
                            {isOverrideOnly ? "업비트 보정" : "코인빌리기 (추정)"}
                        </span>
                        <span className="rounded-md bg-brand-subtle px-1.5 py-0.5 text-xs font-semibold text-brand-light">
                            추정
                        </span>
                    </div>
                    {!editing && (
                        <div className="flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={this.startEdit}
                                disabled={submitting}
                                aria-label="코인빌리기 보정"
                                className="rounded-full border border-surface-border px-2.5 py-1 text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover disabled:opacity-50"
                            >
                                보정
                            </button>
                            <button
                                type="button"
                                onClick={this.armReset}
                                disabled={submitting}
                                aria-label={resetArmed ? "정말 초기화 — 5초 안에 다시 누르면 실행됩니다" : "코인빌리기 초기화"}
                                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                                    resetArmed
                                        ? "border-loss bg-loss-subtle text-loss"
                                        : "border-surface-border text-content-secondary hover:bg-surface-hover"
                                }`}
                            >
                                {resetArmed ? "정말 초기화" : "초기화"}
                            </button>
                        </div>
                    )}
                </div>

                {!editing ? (
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {!isOverrideOnly && (
                            <>
                                <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                                    <div className="text-xs font-semibold text-content-secondary">담보 원화</div>
                                    <div className="mt-1 font-num text-sm font-semibold text-content sm:text-base">
                                        {formatKrw0(loan.collateral_krw)}
                                    </div>
                                </div>
                                {/* DAS-182 계약 보완: 담보 코인 — collateral_coins 가 없거나 빈 배열이면 행 자체를 생략한다 */}
                                {collateralCoinList.length > 0 && (
                                    <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                                        <div className="text-xs font-semibold text-content-secondary">담보 코인</div>
                                        {collateralCoinList.map((c) => (
                                            <div key={c.currency} className="mt-1 font-num text-sm text-content sm:text-base">
                                                {c.currency} {formatQty(c.qty, 8)}개 × {formatKrw0(c.price)} = {formatKrw0(c.value_krw)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                                    <div className="text-xs font-semibold text-content-secondary">담보 합계</div>
                                    <div className="mt-1 font-num text-sm font-semibold text-content sm:text-base">
                                        {formatKrw0(loan.collateral_total_krw)}
                                    </div>
                                </div>
                                <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                                    <div className="text-xs font-semibold text-content-secondary">대여(부채)</div>
                                    {borrowedList.length === 0 ? (
                                        <div className="mt-1 font-num text-sm text-content-muted">—</div>
                                    ) : (
                                        borrowedList.map((b) => (
                                            <div key={b.currency} className="mt-1 font-num text-sm text-content sm:text-base">
                                                {b.currency} {formatQty(b.qty)}개 × {formatKrw0(b.price)} = {formatKrw0(b.value_krw)}
                                            </div>
                                        ))
                                    )}
                                </div>
                                <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                                    <div className="text-xs font-semibold text-content-secondary">순효과</div>
                                    <div className={`mt-1 font-num text-sm font-semibold sm:text-base ${netToneClass(loan.net_krw)}`}>
                                        {formatSignedKrw(loan.net_krw)}
                                    </div>
                                </div>
                            </>
                        )}
                        {/* DAS-182 계약 보완: 평단 보정 — avg_price_override 가 없으면 행 자체를 생략한다 */}
                        {avgPriceOverrideEntries.length > 0 && (
                            <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                                <div className="text-xs font-semibold text-content-secondary">평단 보정</div>
                                {avgPriceOverrideEntries.map(([ccy, price]) => (
                                    <div key={ccy} className="mt-1 font-num text-sm text-content sm:text-base">
                                        {ccy} 보정 {formatKrw0(Number(price))}
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="min-w-0 rounded-lg bg-surface-elevated p-2.5 sm:p-3">
                            <div className="text-xs font-semibold text-content-secondary">마지막 이벤트</div>
                            <div className="mt-1 text-xs text-content-muted sm:text-sm">
                                {lastEventAt ? `${lastEventAt} · ${loan.last_event || "-"}` : "이벤트 없음"}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-lg bg-surface-elevated p-3">
                        {(borrowedList.length > 1 || collateralCoinList.length > 1) && (
                            <p className="mb-3 text-xs text-content-muted">
                                통화가 2개 이상이면 첫 항목만 편집됩니다.
                            </p>
                        )}
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div>
                                <label htmlFor="loan-panel-collateral" className="text-xs font-semibold text-content-secondary">
                                    담보 원화
                                </label>
                                <input
                                    id="loan-panel-collateral"
                                    type="number"
                                    min="0"
                                    value={form.collateralKrw}
                                    onChange={this.handleFieldChange("collateralKrw")}
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                />
                            </div>
                            <div>
                                <label htmlFor="loan-panel-currency" className="text-xs font-semibold text-content-secondary">
                                    대여 통화
                                </label>
                                <select
                                    id="loan-panel-currency"
                                    value={form.currency}
                                    onChange={this.handleFieldChange("currency")}
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                >
                                    <option value="USDT">USDT</option>
                                    <option value="BTC">BTC</option>
                                    <option value="ETH">ETH</option>
                                </select>
                            </div>
                            <div>
                                <label htmlFor="loan-panel-qty" className="text-xs font-semibold text-content-secondary">
                                    대여 수량
                                </label>
                                <input
                                    id="loan-panel-qty"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={form.qty}
                                    onChange={this.handleFieldChange("qty")}
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                />
                            </div>
                        </div>
                        {/* DAS-182 계약 보완: 담보 코인(collateral_coins) 1행 — 수량을 비우면 담보 코인 없이 저장된다 */}
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div>
                                <label htmlFor="loan-panel-collateral-coin-currency" className="text-xs font-semibold text-content-secondary">
                                    담보 코인 통화
                                </label>
                                <select
                                    id="loan-panel-collateral-coin-currency"
                                    value={form.collateralCoinCurrency}
                                    onChange={this.handleFieldChange("collateralCoinCurrency")}
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                >
                                    <option value="">선택 안 함</option>
                                    <option value="BTC">BTC</option>
                                    <option value="ETH">ETH</option>
                                    <option value="USDT">USDT</option>
                                    <option value="OTHER">기타</option>
                                </select>
                            </div>
                            {form.collateralCoinCurrency === "OTHER" && (
                                <div>
                                    <label htmlFor="loan-panel-collateral-coin-custom" className="text-xs font-semibold text-content-secondary">
                                        담보 코인 코드
                                    </label>
                                    <input
                                        id="loan-panel-collateral-coin-custom"
                                        type="text"
                                        value={form.collateralCoinCustom}
                                        onChange={this.handleFieldChange("collateralCoinCustom")}
                                        placeholder="예: SOL"
                                        className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                    />
                                </div>
                            )}
                            <div>
                                <label htmlFor="loan-panel-collateral-coin-qty" className="text-xs font-semibold text-content-secondary">
                                    담보 코인 수량
                                </label>
                                <input
                                    id="loan-panel-collateral-coin-qty"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={form.collateralCoinQty}
                                    onChange={this.handleFieldChange("collateralCoinQty")}
                                    placeholder="비우면 담보 코인 없음"
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content placeholder-content-muted focus:border-brand focus:outline-none"
                                />
                            </div>
                        </div>
                        {/* DAS-182 계약 보완: 평단 보정(avg_price_override) 1행 — 값을 비우거나 0이면 해당 통화 보정을 제거한다 */}
                        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                            <div>
                                <label htmlFor="loan-panel-avg-price-currency" className="text-xs font-semibold text-content-secondary">
                                    평단 보정 통화
                                </label>
                                <select
                                    id="loan-panel-avg-price-currency"
                                    value={form.avgPriceCurrency}
                                    onChange={this.handleFieldChange("avgPriceCurrency")}
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                >
                                    <option value="">선택 안 함</option>
                                    <option value="BTC">BTC</option>
                                    <option value="ETH">ETH</option>
                                    <option value="USDT">USDT</option>
                                    <option value="OTHER">기타</option>
                                </select>
                            </div>
                            {form.avgPriceCurrency === "OTHER" && (
                                <div>
                                    <label htmlFor="loan-panel-avg-price-custom" className="text-xs font-semibold text-content-secondary">
                                        평단 보정 코드
                                    </label>
                                    <input
                                        id="loan-panel-avg-price-custom"
                                        type="text"
                                        value={form.avgPriceCustom}
                                        onChange={this.handleFieldChange("avgPriceCustom")}
                                        placeholder="예: SOL"
                                        className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content focus:border-brand focus:outline-none"
                                    />
                                </div>
                            )}
                            <div>
                                <label htmlFor="loan-panel-avg-price-value" className="text-xs font-semibold text-content-secondary">
                                    평단 보정 값(원)
                                </label>
                                <input
                                    id="loan-panel-avg-price-value"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={form.avgPriceValue}
                                    onChange={this.handleFieldChange("avgPriceValue")}
                                    placeholder="비우면 해당 통화 보정 제거"
                                    className="mt-1 w-full rounded-lg border border-surface-border bg-surface-card px-2 py-1.5 text-sm text-content placeholder-content-muted focus:border-brand focus:outline-none"
                                />
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                            <button
                                type="button"
                                onClick={this.submitEdit}
                                disabled={submitting}
                                className="rounded-full border border-brand bg-brand-subtle px-3 py-1.5 text-xs font-semibold text-brand-light transition-colors hover:bg-brand disabled:opacity-50"
                            >
                                저장
                            </button>
                            <button
                                type="button"
                                onClick={this.cancelEdit}
                                disabled={submitting}
                                className="rounded-full border border-surface-border px-3 py-1.5 text-xs font-medium text-content-secondary transition-colors hover:bg-surface-hover disabled:opacity-50"
                            >
                                취소
                            </button>
                        </div>
                    </div>
                )}

                {(error || statusMessage) && (
                    <p
                        role="status"
                        aria-label={error ? "코인빌리기 오류" : "코인빌리기 상태"}
                        className={`mt-2.5 text-xs ${error ? "text-loss" : "text-content-muted"}`}
                    >
                        {error || statusMessage}
                    </p>
                )}
            </div>
        );
    }
}

export default LoanPanel;
